"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  TextareaAutosize,
  Tabs,
  Tab,
} from "@mui/material"
import {
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from "@mui/icons-material"
import DashboardLayout from "../components/DashboardLayout"
import axios from "axios"
import { API_URL } from "../constants"
import { useSelector } from "react-redux"

// Time selector component
const TimeSelector = ({ label, value, onChange, period, onPeriodChange }) => {
  const [hours, minutes] = value.split(":")

  const handleHoursChange = (direction) => {
    let newHours = Number.parseInt(hours)
    if (direction === "up") {
      newHours = (newHours + 1) % 13
      if (newHours === 0) newHours = 1
    } else {
      newHours = (newHours - 1 + 12) % 13
      if (newHours === 0) newHours = 12
    }
    onChange(`${newHours.toString().padStart(2, "0")}:${minutes}`)
  }

  const handleMinutesChange = (direction) => {
    let newMinutes = Number.parseInt(minutes)
    if (direction === "up") {
      newMinutes = (newMinutes + 1) % 60
    } else {
      newMinutes = (newMinutes - 1 + 60) % 60
    }
    onChange(`${hours}:${newMinutes.toString().padStart(2, "0")}`)
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <IconButton size="small" onClick={() => handleHoursChange("up")}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <TextField
            value={hours}
            InputProps={{
              readOnly: true,
              sx: { width: 60, textAlign: "center", input: { textAlign: "center" } },
            }}
            size="small"
          />
          <IconButton size="small" onClick={() => handleHoursChange("down")}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
        <Typography variant="h6">:</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <IconButton size="small" onClick={() => handleMinutesChange("up")}>
            <KeyboardArrowUpIcon />
          </IconButton>
          <TextField
            value={minutes}
            InputProps={{
              readOnly: true,
              sx: { width: 60, textAlign: "center", input: { textAlign: "center" } },
            }}
            size="small"
          />
          <IconButton size="small" onClick={() => handleMinutesChange("down")}>
            <KeyboardArrowDownIcon />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Button
            variant={period === "AM" ? "contained" : "outlined"}
            size="small"
            onClick={() => onPeriodChange("AM")}
            sx={{ minWidth: 50 }}
          >
            AM
          </Button>
          <Button
            variant={period === "PM" ? "contained" : "outlined"}
            size="small"
            onClick={() => onPeriodChange("PM")}
            sx={{ minWidth: 50, mt: 1 }}
          >
            PM
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const CampaignConfigPage = () => {
  const navigate = useNavigate()
  const { userInfo } = useSelector((state) => state.auth)
  const [configs, setConfigs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [editedConfigs, setEditedConfigs] = useState({})
  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    fetchConfigs()
  }, [])

  // Update the fetchConfigs function to handle empty data better
  const fetchConfigs = async () => {
    setLoading(true)
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      const { data } = await axios.get(`${API_URL}/api/campaign-config`, config)

      if (data && data.length > 0) {
        setConfigs(data)
      } else {
        // If no data, try to initialize
        try {
          await axios.post(`${API_URL}/api/campaign-config/init`, {}, config)
          // Fetch again after initialization
          const { data: newData } = await axios.get(`${API_URL}/api/campaign-config`, config)
          setConfigs(newData)
        } catch (initError) {
          console.error("Failed to initialize configurations:", initError)
          setError("Failed to initialize configurations. Please try again later.")
        }
      }
    } catch (error) {
      // Check if it's a 403 Forbidden error (not admin)
      if (error.response && error.response.status === 403) {
        setError("You do not have permission to access this resource")
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard")
        }, 2000)
      } else {
        setError(error.response?.data?.message || "Failed to fetch configurations")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOpenDialog = () => {
    // Initialize edited configs with current values
    const initialEditedConfigs = {}
    configs.forEach((config) => {
      initialEditedConfigs[config._id] = config.value
    })
    setEditedConfigs(initialEditedConfigs)
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const handleInputChange = (id, e) => {
   
    const value = e?.target?.value
    if (value !== undefined) {
      setEditedConfigs((prev) => ({
        ...prev,
        [id]: value,
      }))
    } else {
      console.error("Event target value is undefined for id:", id)
    }
  }

  const handleTimeChange = (id, value) => {
    const config = configs.find((c) => c._id === id)
    const currentValue = config.value
    const period = currentValue.includes("AM") ? "AM" : "PM"
    setEditedConfigs((prev) => ({
      ...prev,
      [id]: `${value} ${period}`,
    }))
  }

  const handlePeriodChange = (id, period) => {
    const config = configs.find((c) => c._id === id)
    const currentValue = editedConfigs[id] || config.value
    const time = currentValue.split(" ")[0]
    setEditedConfigs((prev) => ({
      ...prev,
      [id]: `${time} ${period}`,
    }))
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      }

      // Prepare configs to update
      const configsToUpdate = configs.map((c) => ({
        _id: c._id,
        value: editedConfigs[c._id] !== undefined ? editedConfigs[c._id] : c.value,
      }))

      const { data } = await axios.put(`${API_URL}/api/campaign-config`, { configs: configsToUpdate }, config)
      setConfigs(data)

      setSuccess("Configurations updated successfully")
      handleCloseDialog()
    } catch (error) {
      setError(error.response?.data?.message || "Failed to update configurations")
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handleCloseAlert = () => {
    setError(null)
    setSuccess(null)
  }

  // Filter configs based on tab
  const getFilteredConfigs = () => {
    if (tabValue === 0) {
      return configs.filter((c) => c.category === "general").sort((a, b) => a.order - b.order)
    } else if (tabValue === 1) {
      return configs.filter((c) => c.category === "domestic").sort((a, b) => a.order - b.order)
    } else {
      return configs.filter((c) => c.category === "international").sort((a, b) => a.order - b.order)
    }
  }

  // Get time and period from time string
  const parseTime = (timeString) => {
    if (!timeString) return { time: "00:00", period: "AM" }

    const parts = timeString.split(" ")
    if (parts.length !== 2) return { time: "00:00", period: "AM" }

    return {
      time: parts[0],
      period: parts[1],
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto" }}>
        <Snackbar
          open={!!error || !!success}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity={error ? "error" : "success"} sx={{ width: "100%" }}>
            {error || success}
          </Alert>
        </Snackbar>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" component="h1">
            Campaign Config
          </Typography>
          <Button variant="contained" color="primary" onClick={handleOpenDialog}>
            Update
          </Button>
        </Box>

        <Paper sx={{ mb: 4 }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tab label="General" />
            <Tab label="Domestic" />
            <Tab label="International" />
          </Tabs>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>SR. NO.</TableCell>
                    <TableCell>NAME</TableCell>
                    <TableCell>SIZE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredConfigs().map((config, index) => (
                    <TableRow key={config._id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{config.name}</TableCell>
                      <TableCell>{config.value}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            Update Campaign Configuration
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="General" />
              <Tab label="Domestic" />
              <Tab label="International" />
            </Tabs>

            {tabValue === 0 && (
              <Grid container spacing={3}>
                {configs
                  .filter((c) => c.category === "general")
                  .map((config) => (
                    <Grid item xs={12} sm={6} key={config._id}>
                      {config.name === "Running Message" ? (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {config.name}
                          </Typography>
                          <TextareaAutosize
                            minRows={3}
                            style={{
                              width: "100%",
                              padding: "8px",
                              borderRadius: "4px",
                              borderColor: "#ccc",
                            }}
                            value={editedConfigs[config._id] || config.value}
                            onChange={(e) => {
                             
                              handleInputChange(config._id, e)
                            }}
                          />
                        </Box>
                      ) : (
                        <TextField
                          fullWidth
                          label={config.name}
                          value={editedConfigs[config._id] || config.value}
                          onChange={(e) => {
                          
                            handleInputChange(config._id, e)
                          }}
                          variant="outlined"
                          size="small"
                          margin="normal"
                        />
                      )}
                    </Grid>
                  ))}
              </Grid>
            )}

            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Domestics
                </Typography>
                <Grid container spacing={3}>
                  {configs
                    .filter((c) => c.category === "domestic" && !c.name.includes("Time"))
                    .map((config) => (
                      <Grid item xs={12} sm={6} key={config._id}>
                        <TextField
                          fullWidth
                          label={config.name}
                          value={editedConfigs[config._id] || config.value}
                          onChange={(e) => {
                          
                            handleInputChange(config._id, e)
                          }}
                          variant="outlined"
                          size="small"
                          margin="normal"
                        />
                      </Grid>
                    ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Max Camp Allowed Message To users
                  </Typography>
                  <TextareaAutosize
                    minRows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      borderColor: "#ccc",
                    }}
                    placeholder="15 Campaign allowed in a day. Please try tomorrow."
                    value={editedConfigs["maxCampMessage"] || "15 Campaign allowed in a day. Please try tomorrow."}
                    onChange={(e) => {
                    
                      handleInputChange("maxCampMessage", e)
                    }}
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept From Time
                  </Typography>
                  <Grid container spacing={3}>
                    {configs
                      .filter((c) => c.category === "domestic" && c.name.includes("From Time"))
                      .map((config) => {
                        const { time, period } = parseTime(editedConfigs[config._id] || config.value)
                        return (
                          <Grid item xs={12} sm={6} key={config._id}>
                            <TimeSelector
                              label={config.name}
                              value={time}
                              onChange={(newTime) => handleTimeChange(config._id, newTime)}
                              period={period}
                              onPeriodChange={(newPeriod) => handlePeriodChange(config._id, newPeriod)}
                            />
                          </Grid>
                        )
                      })}
                  </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept To Time
                  </Typography>
                  <Grid container spacing={3}>
                    {configs
                      .filter((c) => c.category === "domestic" && c.name.includes("To Time"))
                      .map((config) => {
                        const { time, period } = parseTime(editedConfigs[config._id] || config.value)
                        return (
                          <Grid item xs={12} sm={6} key={config._id}>
                            <TimeSelector
                              label={config.name}
                              value={time}
                              onChange={(newTime) => handleTimeChange(config._id, newTime)}
                              period={period}
                              onPeriodChange={(newPeriod) => handlePeriodChange(config._id, newPeriod)}
                            />
                          </Grid>
                        )
                      })}
                  </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept Limit To users
                  </Typography>
                  <TextareaAutosize
                    minRows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      borderColor: "#ccc",
                    }}
                    placeholder="Your daily limit is exceeded..."
                    value={editedConfigs["campLimitMessage"] || "Your daily limit is exceeded..."}
                    onChange={(e) => {
                    
                      handleInputChange("campLimitMessage", e)
                    }}
                  />
                </Box>
              </Box>
            )}

            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  International
                </Typography>
                <Grid container spacing={3}>
                  {configs
                    .filter((c) => c.category === "international" && !c.name.includes("Time"))
                    .map((config) => (
                      <Grid item xs={12} sm={6} key={config._id}>
                        <TextField
                          fullWidth
                          label={config.name}
                          value={editedConfigs[config._id] || config.value}
                          onChange={(e) => {
                          
                            handleInputChange(config._id, e)
                          }}
                          variant="outlined"
                          size="small"
                          margin="normal"
                        />
                      </Grid>
                    ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Max Camp Allowed Message To users
                  </Typography>
                  <TextareaAutosize
                    minRows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      borderColor: "#ccc",
                    }}
                    placeholder="15 Campaign allowed in a day. Please try tomorrow."
                    value={editedConfigs["intMaxCampMessage"] || "15 Campaign allowed in a day. Please try tomorrow."}
                    onChange={(e) => {
                      
                      handleInputChange("intMaxCampMessage", e)
                    }}
                  />
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept From Time
                  </Typography>
                  <Grid container spacing={3}>
                    {configs
                      .filter((c) => c.category === "international" && c.name.includes("From Time"))
                      .map((config) => {
                        const { time, period } = parseTime(editedConfigs[config._id] || config.value)
                        return (
                          <Grid item xs={12} sm={6} key={config._id}>
                            <TimeSelector
                              label={config.name}
                              value={time}
                              onChange={(newTime) => handleTimeChange(config._id, newTime)}
                              period={period}
                              onPeriodChange={(newPeriod) => handlePeriodChange(config._id, newPeriod)}
                            />
                          </Grid>
                        )
                      })}
                  </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept To Time
                  </Typography>
                  <Grid container spacing={3}>
                    {configs
                      .filter((c) => c.category === "international" && c.name.includes("To Time"))
                      .map((config) => {
                        const { time, period } = parseTime(editedConfigs[config._id] || config.value)
                        return (
                          <Grid item xs={12} sm={6} key={config._id}>
                            <TimeSelector
                              label={config.name}
                              value={time}
                              onChange={(newTime) => handleTimeChange(config._id, newTime)}
                              period={period}
                              onPeriodChange={(newPeriod) => handlePeriodChange(config._id, newPeriod)}
                            />
                          </Grid>
                        )
                      })}
                  </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Camp Accept Limit To users
                  </Typography>
                  <TextareaAutosize
                    minRows={3}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: "4px",
                      borderColor: "#ccc",
                    }}
                    placeholder="Your daily limit is exceeded..."
                    value={editedConfigs["intCampLimitMessage"] || "Your daily limit is exceeded..."}
                    onChange={(e) => {
                    
                      handleInputChange("intCampLimitMessage", e)
                    }}
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}

export default CampaignConfigPage
